import React from "react";
import { Helmet } from "react-helmet";

import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import FormSection from "@ats/src/components/forms/FormSection";
import FormSelect from "@ats/src/components/forms/FormSelect";
import FormInput from "@ats/src/components/forms/FormInput";
import FormConditionalFields from "@ats/src/components/forms/FormConditionalFields";
import Button from "@ats/src/components/shared/Button";
import BetaTag from "@ats/src/components/shared/BetaTag";

import { useToastContext } from "@shared/context/ToastContext";
import { useUpdateOrganization } from "@shared/queryHooks/useOrganization";

const enabledDisabledOptions = [
  { value: true, label: "Enabled" },
  { value: false, label: "Disabled" },
];

function OrganizationAiSettings(props: any) {
  const { currentOrganization } = props;
  const addToast = useToastContext();

  const { mutate: updateOrganization, isLoading: isLoadingUpdate } = useUpdateOrganization();

  const [settings, setSettings] = React.useState(currentOrganization?.settings);
  const [errors, setErrors] = React.useState(null);

  React.useEffect(() => {
    setSettings(currentOrganization?.settings);
  }, [currentOrganization]);

  const {
    autoGenerateAiSummariesEnabled,
    hiringTeamAiCreditsControlEnabled,
    lowAiCreditNotificationsEnabled,
    lowAiCreditNotificationThreshold,
    zeroAiCreditNotificationsEnabled,
  } = settings;

  const updateSettingState = (name: string, value: any) => {
    props.setIsDirty(true);
    if (errors != undefined) {
      setErrors(null);
    }
    setSettings({ ...settings, [name]: value });
  };

  const handleOnSubmit = (e: any) => {
    if (e) e.preventDefault();

    updateOrganization(
      {
        id: currentOrganization.id,
        settings: {
          autoGenerateAiSummariesEnabled,
          hiringTeamAiCreditsControlEnabled,
          lowAiCreditNotificationsEnabled,
          lowAiCreditNotificationThreshold: lowAiCreditNotificationsEnabled
            ? lowAiCreditNotificationThreshold
            : 0,
          zeroAiCreditNotificationsEnabled,
        },
      },
      {
        onSuccess: () => {
          props.setIsDirty(false);
          setErrors(null);
          addToast({ title: "Plato AI settings saved", kind: "success" });
        },
        onError: (response: any) => {
          setErrors(response.data.errors);
          addToast({ title: "Could not save settings", kind: "warning" });
        },
      },
    );
  };

  const bottomBarContent = (
    <Button
      className="submit-button"
      styleType="primary"
      onClick={handleOnSubmit}
      loading={isLoadingUpdate}
    >
      Save changes
    </Button>
  );

  return (
    <>
      <Helmet title="Plato AI settings" />

      <SettingsContainer
        title={<>Plato AI settings <BetaTag variant="filledDarkOnly" /></>}
        description="Plato analyzes and scores how your job applicants fit each role. Control if those reviews happen automatically and whether non-admins can generate them."
        bottomBar={bottomBarContent}
        fullWidthForm={true}
      >
        <form onSubmit={handleOnSubmit}>
          <FormSection title="Reviews & credits">
            <FormSelect
              onChange={updateSettingState}
              name="autoGenerateAiSummariesEnabled"
              label="Auto-generate Plato reviews for new applicants"
              description="When enabled, every new applicant automatically gets a Plato review. Each review that generates successfully spends one credit. Individual jobs can override this setting."
              value={autoGenerateAiSummariesEnabled}
              options={enabledDisabledOptions}
              errors={errors}
            />
            <FormSelect
              onChange={updateSettingState}
              name="hiringTeamAiCreditsControlEnabled"
              label="Allow hiring team members to spend credits"
              description="When enabled, hiring team members can generate Plato reviews that spend from your shared credit balance. When disabled, only admins can spend credits."
              value={hiringTeamAiCreditsControlEnabled}
              options={enabledDisabledOptions}
              errors={errors}
            />
          </FormSection>

          <FormSection title="Notifications">
            <FormSelect
              onChange={updateSettingState}
              name="lowAiCreditNotificationsEnabled"
              label="Notify when credits are running low"
              description="Email all organization admins once your balance falls below the threshold you set, so reviews never quietly stop mid-pipeline."
              value={lowAiCreditNotificationsEnabled}
              options={enabledDisabledOptions}
              errors={errors}
            />

            {lowAiCreditNotificationsEnabled && (
              <FormConditionalFields>
                <FormInput
                  onChange={updateSettingState}
                  name="lowAiCreditNotificationThreshold"
                  label="Notify when the balance drops below"
                  type="number"
                  min={1}
                  value={lowAiCreditNotificationThreshold}
                  errors={errors}
                />
              </FormConditionalFields>
            )}

            <FormSelect
              onChange={updateSettingState}
              name="zeroAiCreditNotificationsEnabled"
              label="Notify when credits hit zero"
              description="A final alert the moment Plato can't generate any more reviews. Useful even if the low-credit warning is turned off."
              value={zeroAiCreditNotificationsEnabled}
              options={enabledDisabledOptions}
              errors={errors}
            />
          </FormSection>
        </form>
      </SettingsContainer>
    </>
  );
}

export default OrganizationAiSettings;
